import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'sonner';
import {
  Bookmark,
  X,
  GripVertical,
  ExternalLink,
  Trash2,
  Plus,
  Youtube,
  Loader2,
  Link as LinkIcon,
} from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChannelReferencesModal = ({ isOpen, onClose, canalId, canalName, onReferencesChanged }) => {
  const { t } = useTranslation();
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // New reference form
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [adding, setAdding] = useState(false);

  // Fetch references when modal opens
  const fetchReferences = useCallback(async () => {
    if (!canalId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/referencias/canal/${canalId}`);
      setReferences(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao buscar referências:', error);
      toast.error(t('refs_modal.error_load'));
    } finally {
      setLoading(false);
    }
  }, [canalId, t]);

  useEffect(() => {
    if (isOpen && canalId) {
      fetchReferences();
      setNewName('');
      setNewUrl('');
      setDeleteConfirmId(null);
    }
  }, [isOpen, canalId, fetchReferences]);

  // ESC to close
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Drag & drop reorder (local only — visual reorder)
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(references);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setReferences(items);
  };

  // Add new reference
  const handleAdd = async () => {
    if (!newName.trim() && !newUrl.trim()) return;

    setAdding(true);
    try {
      const response = await axios.post(`${API}/referencias`, {
        nome_canal: newName.trim() || 'Canal',
        url_canal: newUrl.trim() || null,
        foto_url: null,
        nicho: null,
        anotacoes: null,
        canal_id: canalId,
      });

      // Add to local list
      setReferences(prev => [...prev, response.data]);
      setNewName('');
      setNewUrl('');
      toast.success(`✅ ${t('refs_modal.added')}`);
      if (onReferencesChanged) onReferencesChanged();
    } catch (error) {
      console.error('Erro ao adicionar referência:', error);
      toast.error(t('refs_modal.error_add'));
    } finally {
      setAdding(false);
    }
  };

  // Delete reference
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/referencias/${id}`);
      setReferences(prev => prev.filter(r => r.id !== id));
      setDeleteConfirmId(null);
      toast.success(`🗑️ ${t('refs_modal.deleted')}`);
      if (onReferencesChanged) onReferencesChanged();
    } catch (error) {
      console.error('Erro ao excluir referência:', error);
      toast.error(t('refs_modal.error_delete'));
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        animation: 'dp-modal-overlay 0.2s ease-out',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 'calc(100% - 32px)', maxWidth: '540px', maxHeight: '85vh',
          margin: '16px',
          background: 'var(--dp-bg-glass, rgba(28,28,28,0.95))',
          backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid var(--dp-border-medium)',
          borderRadius: '16px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column',
          animation: 'dp-modal-scale 0.25s ease-out',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--dp-border-subtle)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '10px',
                background: 'rgba(239, 68, 68, 0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Bookmark size={18} style={{ color: 'var(--dp-red-500, #F87171)' }} />
              </div>
              <div>
                <h2 style={{
                  fontSize: '16px', fontWeight: 700, color: 'var(--dp-text-primary)',
                  margin: 0, fontFamily: 'var(--dp-font)',
                }}>
                  {t('refs_modal.title')}
                </h2>
                <p style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', margin: '2px 0 0' }}>
                  {canalName || ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'transparent', border: '1px solid var(--dp-border-subtle)',
                borderRadius: '8px', padding: '6px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-red-500, #F87171)'; e.currentTarget.style.background = 'rgba(248,113,113,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.background = 'transparent'; }}
            >
              <X size={16} style={{ color: 'var(--dp-text-tertiary)' }} />
            </button>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', margin: '8px 0 0', lineHeight: 1.4 }}>
            {t('refs_modal.subtitle')}
          </p>
        </div>

        {/* Body (scrollable) */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[1, 2, 3].map(i => (
                <div key={i} className="dp-skeleton" style={{ height: '52px', borderRadius: '10px' }} />
              ))}
            </div>
          ) : (
            <>
              {/* Channel count */}
              {references.length > 0 && (
                <div style={{
                  fontSize: '12px', fontWeight: 600, color: 'var(--dp-text-secondary)',
                  marginBottom: '10px', fontFamily: 'var(--dp-font)',
                }}>
                  {t('refs_modal.channels_added', { count: references.length })}
                </div>
              )}

              {references.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                  <Youtube size={36} style={{ color: 'var(--dp-text-tertiary)', margin: '0 auto 10px', opacity: 0.4 }} />
                  <p style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)' }}>
                    {t('refs_modal.empty')}
                  </p>
                </div>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="refs-list">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {references.map((ref, index) => (
                          <Draggable key={ref.id} draggableId={ref.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  background: snapshot.isDragging ? 'rgba(239, 68, 68, 0.06)' : 'rgba(255,255,255,0.02)',
                                  border: `1px solid ${snapshot.isDragging ? 'rgba(239, 68, 68, 0.25)' : 'var(--dp-border-subtle)'}`,
                                  borderRadius: '10px',
                                  padding: '10px 12px',
                                  display: 'flex', alignItems: 'center', gap: '10px',
                                  transition: snapshot.isDragging ? 'none' : 'all 0.2s ease',
                                }}
                              >
                                {/* Drag handle */}
                                <div {...provided.dragHandleProps} style={{ cursor: 'grab', flexShrink: 0, display: 'flex' }}>
                                  <GripVertical size={15} style={{ color: 'var(--dp-text-tertiary)', opacity: 0.5 }} />
                                </div>

                                {/* Avatar */}
                                {ref.foto_url ? (
                                  <img
                                    src={ref.foto_url}
                                    alt={ref.nome_canal}
                                    style={{
                                      width: '32px', height: '32px', borderRadius: '50%',
                                      objectFit: 'cover', flexShrink: 0,
                                      border: '1px solid var(--dp-border-subtle)',
                                    }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div style={{
                                    width: '32px', height: '32px', borderRadius: '50%',
                                    background: 'rgba(239, 68, 68, 0.10)', flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}>
                                    <Youtube size={16} style={{ color: 'var(--dp-red-500, #F87171)' }} />
                                  </div>
                                )}

                                {/* Name + URL */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{
                                    fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)',
                                    fontFamily: 'var(--dp-font)',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                  }}>
                                    {ref.nome_canal}
                                  </div>
                                  {ref.url_canal && (
                                    <div style={{
                                      fontSize: '11px', color: 'var(--dp-text-tertiary)',
                                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                      marginTop: '1px',
                                    }}>
                                      {ref.url_canal}
                                    </div>
                                  )}
                                </div>

                                {/* Actions */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                  {/* External link */}
                                  {ref.url_canal && (
                                    <button
                                      onClick={() => window.open(ref.url_canal, '_blank')}
                                      title={t('refs_modal.open_youtube')}
                                      style={{
                                        background: 'transparent', border: 'none',
                                        borderRadius: '6px', padding: '5px', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        transition: 'all 0.15s ease',
                                      }}
                                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.10)'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                    >
                                      <ExternalLink size={14} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
                                    </button>
                                  )}

                                  {/* Delete */}
                                  {deleteConfirmId === ref.id ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <button
                                        onClick={() => handleDelete(ref.id)}
                                        style={{
                                          background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)',
                                          borderRadius: '6px', padding: '3px 8px', cursor: 'pointer',
                                          fontSize: '10px', fontWeight: 600, color: 'var(--dp-red-500, #F87171)',
                                          fontFamily: 'var(--dp-font)',
                                        }}
                                      >
                                        {t('refs_modal.confirm_yes')}
                                      </button>
                                      <button
                                        onClick={() => setDeleteConfirmId(null)}
                                        style={{
                                          background: 'transparent', border: '1px solid var(--dp-border-subtle)',
                                          borderRadius: '6px', padding: '3px 8px', cursor: 'pointer',
                                          fontSize: '10px', fontWeight: 500, color: 'var(--dp-text-tertiary)',
                                          fontFamily: 'var(--dp-font)',
                                        }}
                                      >
                                        {t('refs_modal.confirm_no')}
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setDeleteConfirmId(ref.id)}
                                      title={t('refs_modal.remove')}
                                      style={{
                                        background: 'transparent', border: 'none',
                                        borderRadius: '6px', padding: '5px', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        transition: 'all 0.15s ease',
                                      }}
                                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.10)'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                    >
                                      <X size={14} style={{ color: 'var(--dp-red-500, #F87171)' }} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}

              {/* Add New Section */}
              <div style={{
                marginTop: '16px', padding: '14px',
                borderRadius: '12px',
                border: '1px dashed var(--dp-border-medium)',
                background: 'rgba(255,255,255,0.01)',
              }}>
                <div style={{
                  fontSize: '12px', fontWeight: 600, color: 'var(--dp-text-secondary)',
                  marginBottom: '10px', fontFamily: 'var(--dp-font)',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                  <Plus size={14} style={{ color: 'var(--dp-red-500, #F87171)' }} />
                  {t('refs_modal.add_section_title')}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder={t('refs_modal.name_placeholder')}
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: '8px',
                      border: '1px solid var(--dp-border-subtle)',
                      background: 'var(--dp-bg-input, #2E2E2E)',
                      color: 'var(--dp-text-primary)', fontSize: '13px',
                      fontFamily: 'var(--dp-font)', outline: 'none',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = 'var(--dp-red-500, #F87171)'; e.target.style.boxShadow = '0 0 0 3px rgba(239,68,68,0.10)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; e.target.style.boxShadow = 'none'; }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="url"
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      placeholder={t('refs_modal.url_placeholder')}
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: '8px',
                        border: '1px solid var(--dp-border-subtle)',
                        background: 'var(--dp-bg-input, #2E2E2E)',
                        color: 'var(--dp-text-primary)', fontSize: '13px',
                        fontFamily: 'var(--dp-font)', outline: 'none',
                        transition: 'border-color 0.2s, box-shadow 0.2s',
                        boxSizing: 'border-box',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = 'var(--dp-red-500, #F87171)'; e.target.style.boxShadow = '0 0 0 3px rgba(239,68,68,0.10)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; e.target.style.boxShadow = 'none'; }}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                    />
                    <button
                      onClick={handleAdd}
                      disabled={adding || (!newName.trim() && !newUrl.trim())}
                      style={{
                        padding: '8px 16px', borderRadius: '8px', cursor: (newName.trim() || newUrl.trim()) ? 'pointer' : 'default',
                        border: 'none',
                        background: (newName.trim() || newUrl.trim())
                          ? 'linear-gradient(135deg, var(--dp-red-500, #F87171), #EF4444)'
                          : 'rgba(255,255,255,0.06)',
                        color: (newName.trim() || newUrl.trim()) ? '#fff' : 'var(--dp-text-tertiary)',
                        fontSize: '12px', fontWeight: 600, fontFamily: 'var(--dp-font)',
                        display: 'flex', alignItems: 'center', gap: '4px',
                        transition: 'all 0.2s ease', flexShrink: 0,
                        opacity: (newName.trim() || newUrl.trim()) ? 1 : 0.5,
                      }}
                    >
                      {adding ? (
                        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <Plus size={14} />
                      )}
                      {t('refs_modal.add_button')}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--dp-border-subtle)',
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 24px', borderRadius: '8px', cursor: 'pointer',
              border: '1px solid var(--dp-border-medium)',
              background: 'transparent', color: 'var(--dp-text-secondary)',
              fontSize: '13px', fontWeight: 500, fontFamily: 'var(--dp-font)',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            {t('refs_modal.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChannelReferencesModal;
